import { useMemo } from 'react'
import { TableBody, TableRow, TableCell, useTheme } from '@mui/material'
import TaskStateChip from '../TaskStateChip'
import { formatTaskCreationDate, getComparator } from '../../utils'
import { useParams } from 'react-router-dom'
import PopupMenuList from './PopupMenuList'
import { EnhancedTableBodyProps } from '@features/reader_task_list_iam_view/pages/reader_task_list.types'
import ReopenModal from '../modals/ReopenModal'
import { User } from '@common/config/api/client'
import TaskTypeChip from '@reader_task_list_view/components/TaskTypeChip'
import { calculateTimeSpent } from '@reader_task_list_view/utils'
import { getGcisViewerUrl } from '@common/utils/ohifUtils'
import ReaderSummaryModal from '@features/reader_summary/pages/pages/ReaderSummaryModal'
import { useState } from 'react'

export default function EnhancedTableBodyComplete({
  rows,
  order,
  orderBy,
  defaultViewerIndex = 0,
}: EnhancedTableBodyProps) {
  const visibleRows = useMemo(() => {
    const comparator = getComparator(order, orderBy)
    return [...rows].sort(comparator)
  }, [order, orderBy, rows])

  const { studyId } = useParams()
  const theme = useTheme()
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>()

  const handleRowClick = (task_id: string) => {
    const url = getGcisViewerUrl(task_id, studyId!, defaultViewerIndex)
    if (url) openInNewTab(url)
  }

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      <TableBody>
        {visibleRows.map((row) => {
          const assignedUser: User | undefined =
            row.assignee !== null && row.assignee.user_id !== null && row.assignee.user_name !== null
              ? {
                  user_id: row.assignee?.user_id,
                  user_name: row.assignee?.user_name,
                }
              : undefined
          return (
            <TableRow
              tabIndex={-1}
              key={row.task_id}
              onClick={(e) => {
                e.stopPropagation()
                handleRowClick(row.task_id)
              }}
              sx={{
                cursor: 'pointer',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <TableCell align="left" data-testid="subject-pseudo-id-cell">
                {row.subject_id}
              </TableCell>
              <TableCell align="left" data-testid="task-blind-code-cell">
                {row.blind_code}
              </TableCell>
              <TableCell align="left" data-testid="timepoint-name-cell">
                {row.timepoint_pseudo_name}
              </TableCell>
              <TableCell align="left" data-testid="task-pseudo-id-cell">
                {row.task_pseudo_id}
              </TableCell>
              <TableCell align="left" data-testid="task-type-cell">
                <TaskTypeChip taskType={row.task_type} />
              </TableCell>
              <TableCell align="left" data-testid="task-priority-cell">
                {row.priority}
              </TableCell>
              <TableCell align="left" data-testid="task-state-cell">
                <TaskStateChip taskState={row.status} />
              </TableCell>

              <TableCell align="left" data-testid="create-date-cell">
                {formatTaskCreationDate(row.created_at)}
              </TableCell>

              <TableCell align="left" data-testid="complete-date-cell">
                {formatTaskCreationDate(row.completed_date ?? '')}
              </TableCell>

              <TableCell align="left" data-testid="time-spent-cell">
                {calculateTimeSpent(row.reader_active_time || 0)}
              </TableCell>

              <TableCell align="left" data-testid="assignee-cell">
                {row.assignee?.user_name}
              </TableCell>

              <TableCell align="right" data-testid="actions-cell">
                {row.status === 'REOPENED_PENDING_APPROVAL' ? (
                  <PopupMenuList
                    task_id={row.task_id}
                    task_status={row.status}
                    read_pseudo_id={row.task_pseudo_id}
                    study_id={studyId!}
                    comment={row.status_change_reason ?? undefined}
                    reader={assignedUser}
                  />
                ) : (
                  <ReopenModal read_pseudo_id={row.task_pseudo_id} study_id={studyId!} read_task_id={row.task_id} />
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
      <ReaderSummaryModal
        open={!!selectedTaskId}
        onClose={() => setSelectedTaskId(undefined)}
        taskId={selectedTaskId}
        studyId={studyId}
      />
    </>
  )
}
